import React, { useState } from 'react';
import { Alert, Box, Button, Link, Stack, Tooltip, Typography } from '@mui/material';
import { ContentCopy as CopyIcon, Check as CheckIcon, OpenInNew as OpenIcon } from '@mui/icons-material';

export const formatFecha = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('es-CO', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const estadoInfo = (code) => {
  if (!code) return { severity: 'info', texto: '' };
  switch (code.estado) {
    case 'pendiente':
      return { severity: 'info', texto: `Se activa el ${formatFecha(code.activates_at)}` };
    case 'activo':
      return {
        severity: 'success',
        texto: code.expires_at ? `Vigente hasta ${formatFecha(code.expires_at)}` : 'Vigente',
      };
    case 'vencido':
      return { severity: 'error', texto: 'Vencido: contacta a la administración' };
    case 'revocado':
      return { severity: 'error', texto: 'Código revocado: contacta a la administración' };
    default:
      return { severity: 'info', texto: '' };
  }
};

const copiar = async (texto) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(texto);
      return true;
    }
  } catch (e) {
    // cae al método antiguo
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = texto;
    ta.setAttribute('readonly', '');
    ta.style.position = 'absolute';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch (e) {
    return false;
  }
};

/**
 * Cuerpo compartido por el modal y la tarjeta: código en grande, botón
 * "Copiar", estado, instrucciones y enlace a ActionEV.
 */
const MigrationCodeContent = ({ code, compact = false }) => {
  const [copiado, setCopiado] = useState(false);
  if (!code) return null;

  const { severity, texto } = estadoInfo(code);
  const mostrarCodigo = code.estado !== 'revocado';

  const handleCopy = async () => {
    const ok = await copiar(code.codigo);
    if (ok) {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    }
  };

  return (
    <Stack spacing={compact ? 1.5 : 2}>
      {mostrarCodigo && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            p: compact ? 1.5 : 2.5,
            borderRadius: 2,
            backgroundColor: '#F0F6FA',
            border: '1px dashed #00577A',
          }}
        >
          <Typography
            component="span"
            sx={{
              fontFamily: 'monospace',
              fontWeight: 700,
              letterSpacing: '0.12em',
              fontSize: compact ? { xs: '1.5rem', sm: '1.9rem' } : { xs: '1.8rem', sm: '2.4rem' },
              color: '#002642',
              userSelect: 'all',
            }}
            data-testid="migration-code"
          >
            {code.codigo}
          </Typography>
          <Tooltip title={copiado ? 'Copiado' : 'Copiar al portapapeles'}>
            <Button
              size="small"
              variant={copiado ? 'contained' : 'outlined'}
              color={copiado ? 'success' : 'primary'}
              startIcon={copiado ? <CheckIcon /> : <CopyIcon />}
              onClick={handleCopy}
            >
              {copiado ? 'Copiado' : 'Copiar'}
            </Button>
          </Tooltip>
        </Box>
      )}

      {texto && <Alert severity={severity} sx={{ borderRadius: 2 }}>{texto}</Alert>}

      <Typography variant="body2" sx={{ color: '#3C4043', lineHeight: 1.6 }}>
        Entra a{' '}
        <Link href={code.url_registro} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 600 }}>
          {code.url_registro}
        </Link>
        , elige tu sede y escribe este código. Si ya tienes cuenta en ActionEV, usa{' '}
        <strong>&quot;Unirme a una sede con código&quot;</strong>.
      </Typography>

      <Box>
        <Button
          variant="contained"
          endIcon={<OpenIcon />}
          href={code.url_registro}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ backgroundColor: '#002642', '&:hover': { backgroundColor: '#00577A' } }}
        >
          Ir a ActionEV
        </Button>
      </Box>
    </Stack>
  );
};

export default MigrationCodeContent;
